/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { createLogger, EventEmitter, LoggerLevel, Logger } from '@quickplay/foundation';
import { AdComposedPlayer, AdEvent, PlayerContext } from '@quickplay/player-interface';
import { getAdfrequencyController } from '../adfrequencyControl/AFCFactory';

/** Seconds before a mid-roll cue point at which the "ad incoming" countdown starts. */
const DEFAULT_LEAD_TIME_SEC = 5;

/**
 * Payload published on `AdEvent.ADVERT_INCOMING`.
 * - `show`: an ad break starts in `remainingSeconds` seconds; the app can render a countdown.
 * - `hide`: there is no longer an incoming ad break; the app should remove the countdown.
 */
export type AdIncomingEvent = { action: 'show'; remainingSeconds: number } | { action: 'hide' };

/**
 * Tells the application shortly before an eligible VOD mid-roll ad break begins.
 *
 * How it works:
 * 1. Listens to playback progress and to the list of ad cue points.
 * 2. When the playhead gets within `leadTimeSec` of the next cue point, and ad frequency control (AFC)
 *    allows an ad opportunity, it publishes a `show` event with the remaining seconds (once per second change).
 * 3. When the countdown no longer applies (seeking, ad playing, no eligible cue point, AFC blocks), it
 *    publishes a single `hide` event.
 */
export class AdIncomingNotifier {
  private readonly player: AdComposedPlayer;

  private readonly leadTimeSec: number;

  private readonly eventEmitter: EventEmitter;

  private readonly logger: Logger;

  private cuePoints: number[] = [];

  private lastPublishedRemainingSeconds: number | null = null;

  private lastPublishedAction: AdIncomingEvent['action'] | null = null;

  /** True after `destroy()`. Prevents any further processing or publishing. */
  private disposed = false;

  /**
   * @param player Player to observe.
   * @param eventEmitter Emitter on which incoming-ad events are published.
   * @param leadTimeMs Optional countdown lead time in milliseconds. Must be a positive finite number,
   *   otherwise `DEFAULT_LEAD_TIME_SEC` is used.
   */
  constructor(player: AdComposedPlayer, eventEmitter: EventEmitter, leadTimeMs?: number, logger?: Logger) {
    if (!player) {
      throw new Error('AdIncomingNotifier requires a valid player instance.');
    }

    this.player = player;
    this.eventEmitter = eventEmitter;
    this.logger = logger || createLogger(LoggerLevel.OFF);
    this.leadTimeSec =
      leadTimeMs && Number.isFinite(leadTimeMs) && leadTimeMs > 0 ? leadTimeMs / 1000 : DEFAULT_LEAD_TIME_SEC;

    this.subscribeToPlayerEvents();
  }

  /**
   * Stops observing the player and clears internal state. Call when the player is released.
   * Safe to call more than once.
   */
  public destroy(): void {
    if (this.disposed) return;

    this.disposed = true;
    this.unsubscribeFromPlayerEvents();

    this.lastPublishedRemainingSeconds = null;
    this.lastPublishedAction = null;
  }

  /** Starts listening to playback progress and cue point updates. */
  private subscribeToPlayerEvents(): void {
    this.player.subscribe('progressupdate', this.handleProgressUpdate);
    this.player.subscribe(AdEvent.ADVERT_CUEPOINTS_UPDATED, this.handleCuePointsUpdated);
  }

  /** Stops listening to playback progress and cue point updates. */
  private unsubscribeFromPlayerEvents(): void {
    this.player.unsubscribe('progressupdate', this.handleProgressUpdate);
    this.player.unsubscribe(AdEvent.ADVERT_CUEPOINTS_UPDATED, this.handleCuePointsUpdated);
  }

  /**
   * Runs on every playback progress update and decides whether to show, update or hide the countdown.
   *
   * - Ad is playing or the user is seeking: hides the countdown.
   * - No eligible cue point inside the lead window: hides the countdown if it is currently shown.
   * - Eligible cue point inside the lead window: publishes the remaining seconds when they changed.
   *
   * @param progressTime Playhead time from the event, used only if the player does not expose `currentTime`.
   */
  private readonly handleProgressUpdate = (progressTime?: number): void => {
    if (this.disposed) return;

    if (this.isCountdownInterrupted()) {
      this.stopCountdown();
      return;
    }

    const playheadTime = this.player.currentTime ?? progressTime;
    if (playheadTime === undefined || !Number.isFinite(playheadTime)) return;

    const upcomingCuePoint = this.findCuePointInLeadWindow(playheadTime);

    if (upcomingCuePoint === null) {
      if (this.lastPublishedAction === 'show') {
        this.logger?.log('Hiding countdown as there is no upcoming cue point.');
        this.stopCountdown();
      }
      return;
    }

    const remainingSeconds = Math.max(0, Math.ceil(upcomingCuePoint - playheadTime));
    if (remainingSeconds === this.lastPublishedRemainingSeconds) return;

    this.lastPublishedRemainingSeconds = remainingSeconds;
    this.publishShowEvent(remainingSeconds);
  };

  /**
   * Returns the next cue point after the playhead if it is close enough to show the countdown
   * and AFC allows an ad opportunity. Returns `null` otherwise.
   *
   * Only the first cue point after the playhead is checked, because `cuePoints` is sorted and every later
   * cue point is even farther away. AFC is asked only when the cue point is inside the lead window.
   *
   * @param playheadTime Current playback position in seconds.
   */
  private findCuePointInLeadWindow(playheadTime: number): number | null {
    const nextCuePoint = this.cuePoints.find(cuePoint => cuePoint > playheadTime);
    if (nextCuePoint === undefined) return null;

    const isInsideLeadWindow = nextCuePoint - playheadTime <= this.leadTimeSec;

    return isInsideLeadWindow && getAdfrequencyController().evaluateOpportunity() ? nextCuePoint : null;
  }

  /**
   * Stores adjusted transition timestamps. Pre-roll `0`, post-roll `-1`, and
   * transition timestamps at or before zero are excluded. Order is preserved
   * from `cuePoints`, so the player must report them in ascending order.
   *
   * @param cuePoints Cue point times in seconds, as reported by the player.
   * @param adCueTransitionDelayMs Optional ad cue transition delay in milliseconds.
   */
  private readonly handleCuePointsUpdated = (cuePoints: number[], adCueTransitionDelayMs?: number): void => {
    const transitionDelaySec =
      typeof adCueTransitionDelayMs === 'number' &&
      Number.isFinite(adCueTransitionDelayMs) &&
      adCueTransitionDelayMs > 0
        ? adCueTransitionDelayMs / 1000
        : 0;

    const normalizedCuePoints: number[] = [];

    if (Array.isArray(cuePoints)) {
      for (const cuePoint of cuePoints) {
        const transitionTime = cuePoint - transitionDelaySec;
        // Rejects NaN/Infinity, pre-roll (0), post-roll (-1), and anything at or before start.
        if (Number.isFinite(transitionTime) && transitionTime > 0) {
          normalizedCuePoints.push(transitionTime);
        }
      }
    }
    normalizedCuePoints.sort((a, b) => a - b);

    this.cuePoints = normalizedCuePoints;

    this.logger?.log(`Normalized transition cue points: ${JSON.stringify(normalizedCuePoints)}`);

    if (normalizedCuePoints.length === 0) {
      if (this.lastPublishedAction === 'show') {
        this.logger?.log('No normalized cue points available. Stopping countdown.');
        this.stopCountdown();
      }

      this.destroy();
    }
  };

  /** Clears the remaining-seconds tracking and tells the application to hide the countdown. */
  private stopCountdown(): void {
    this.lastPublishedRemainingSeconds = null;
    this.publishHideEvent();
  }

  /**
   * Tells the application to show or update the countdown.
   *
   * @param remainingSeconds Whole seconds left until the ad break starts.
   */
  private publishShowEvent(remainingSeconds: number): void {
    this.lastPublishedAction = 'show';
    this.eventEmitter.publish(AdEvent.ADVERT_INCOMING, { action: 'show', remainingSeconds });
  }

  /** Tells the application to hide the countdown. Does nothing if `hide` was already the last event published. */
  private publishHideEvent(): void {
    if (this.lastPublishedAction === 'hide') return;

    this.lastPublishedAction = 'hide';
    this.eventEmitter.publish(AdEvent.ADVERT_INCOMING, { action: 'hide' });
  }
  /** True while an ad is playing, or while the user is seeking and there are cue points to track. */
  private isCountdownInterrupted(): boolean {
    const isAdPlaying = this.player.playerContext === PlayerContext.AD;
    const isSeekingWithCuePoints = this.cuePoints.length > 0 && this.player.isSeeking;
    return isAdPlaying || isSeekingWithCuePoints;
  }
}
